import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Checkbox, Input, Message, Modal, Radio, Select, Space, Table, Tag, Tooltip, Typography } from "@arco-design/web-react";
import { IconRefresh, IconApps, IconList, IconEdit } from "@arco-design/web-react/icon";
import Content from "../Layout/Content";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, OverwriteApi } from "../Api/AuthenticatedApi";
import CardDrawer from "./CardDrawer";

const { Text } = Typography;
const { TextArea } = Input;

export default function WorkflowDetail() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [workflow, setWorkflow] = useState(null);
  const [stages, setStages] = useState([]);
  const [cards, setCards] = useState([]);
  const [members, setMembers] = useState([]);
  const [myUserId, setMyUserId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // View + filters
  const [viewMode, setViewMode] = useState("board"); // "board" | "list"
  const [hideTerminal, setHideTerminal] = useState(true);
  const [myCardsOnly, setMyCardsOnly] = useState(false);

  // Drag feedback
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);

  // Card drawer
  const [drawerCard, setDrawerCard] = useState(null);

  // Required-note move modal: holds the pending move until a note is supplied.
  const [pendingMove, setPendingMove] = useState(null); // { cardId, stageId }
  const [moveNote, setMoveNote] = useState("");

  // Edit-workflow modal
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editTrackValue, setEditTrackValue] = useState(false);
  const [editAssignees, setEditAssignees] = useState([]);

  const base = "/api/workflows/" + workflowId;

  const fetchCards = useCallback(() => {
    FetchApi(getAccessTokenSilently, base + "/cards",
      (data) => setCards(data || []),
      () => navigate("/login"),
      () => Message.error("Failed to load cards"));
  }, [getAccessTokenSilently, navigate, base]);

  // Load workflow, then its pipeline's stages; plus members + current user.
  useEffect(() => {
    FetchApi(getAccessTokenSilently, base,
      (wf) => {
        setWorkflow(wf);
        if (wf && wf.pipeline_id) {
          FetchApi(getAccessTokenSilently, "/api/pipelines/" + wf.pipeline_id,
            (p) => setStages((p && p.stages) || []),
            () => navigate("/login"), () => {});
        }
      },
      () => navigate("/login"),
      () => Message.error("Failed to load workflow"));
    FetchApi(getAccessTokenSilently, "/api/members/", (data) => setMembers(data || []), () => {}, () => {});
    FetchApi(getAccessTokenSilently, "/api/me", (data) => setMyUserId(data && data.user && data.user.id), () => {}, () => {});
    fetchCards();
  }, [getAccessTokenSilently, navigate, base, fetchCards]);

  const memberName = (userId) => {
    const m = members.find((x) => x.id === userId);
    return m ? (m.name || m.email) : null;
  };

  const openCard = (cardId) => {
    FetchApi(getAccessTokenSilently, base + "/cards/" + cardId,
      (data) => setDrawerCard(data),
      () => navigate("/login"),
      () => Message.error("Failed to load card"));
  };

  const refreshAfterCardChange = (updatedCard) => {
    fetchCards();
    if (updatedCard) setDrawerCard(updatedCard);
  };

  const doMove = (cardId, stageId, note) => {
    PostApi(getAccessTokenSilently, base + "/cards/" + cardId + "/move",
      (updated) => { refreshAfterCardChange(drawerCard && drawerCard.id === cardId ? updated : null); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to move card"),
      { to_stage_id: stageId, note: note || "" });
  };

  // Move, prompting for a note first if the target stage requires one.
  const attemptMove = (cardId, stageId) => {
    const card = cards.find((c) => c.id === cardId) || (drawerCard && drawerCard.id === cardId ? drawerCard : null);
    if (card && card.current_stage_id === stageId) return;
    const stage = stages.find((s) => s.id === stageId);
    if (stage && stage.requires_note) {
      setMoveNote("");
      setPendingMove({ cardId, stageId });
      return;
    }
    doMove(cardId, stageId, "");
  };

  const confirmNoteMove = () => {
    if (!moveNote.trim()) { Message.warning("A note is required for this stage"); return; }
    doMove(pendingMove.cardId, pendingMove.stageId, moveNote.trim());
    setPendingMove(null);
  };

  const addNote = (cardId, note) => {
    PostApi(getAccessTokenSilently, base + "/cards/" + cardId + "/notes",
      (updated) => refreshAfterCardChange(updated),
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to add note"),
      { note });
  };

  const setOwner = (cardId, userId) => {
    OverwriteApi(getAccessTokenSilently, base + "/cards/" + cardId + "/owner",
      (updated) => refreshAfterCardChange(updated),
      () => navigate("/login"),
      () => Message.error("Failed to set owner"),
      { user_id: userId || "" });
  };

  const setValue = (cardId, amount, expectedClose, probability) => {
    OverwriteApi(getAccessTokenSilently, base + "/cards/" + cardId + "/value",
      (updated) => { Message.success("Deal saved"); refreshAfterCardChange(updated); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to save deal"),
      { amount, expected_close_date: expectedClose, probability });
  };

  const openEdit = () => {
    if (!workflow) return;
    setEditName(workflow.name || "");
    setEditTrackValue(!!workflow.track_value);
    setEditAssignees(workflow.assignee_user_ids || []);
    setEditOpen(true);
  };

  const saveEdit = () => {
    if (!editName.trim()) { Message.warning("Name is required"); return; }
    OverwriteApi(getAccessTokenSilently, base,
      (updated) => { setWorkflow(updated); setEditOpen(false); Message.success("Workflow updated"); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to update workflow"),
      {
        name: editName.trim(),
        track_value: editTrackValue,
        assignee_user_ids: editAssignees,
        reentry_policy: workflow && workflow.reentry_policy,
      });
  };

  const syncNow = () => {
    setSyncing(true);
    PostApi(getAccessTokenSilently, base + "/reconcile",
      (data) => {
        setSyncing(false);
        const n = (data && data.enrolled) || 0;
        Message.success(n > 0 ? `Enrolled ${n} new contact${n === 1 ? "" : "s"}` : "Already up to date");
        if (n > 0) fetchCards();
      },
      () => navigate("/login"),
      () => { setSyncing(false); Message.error("Failed to sync enrollment"); });
  };

  const terminalStageIds = new Set(stages.filter((s) => s.is_terminal).map((s) => s.id));
  const cardMatchesFilter = (c) =>
    (!myCardsOnly || c.owner_user_id === myUserId) &&
    (!hideTerminal || !terminalStageIds.has(c.current_stage_id));

  const visibleStages = stages.filter((s) => !hideTerminal || !s.is_terminal);
  const cardsForStage = (stageId) => cards.filter((c) => c.current_stage_id === stageId && cardMatchesFilter(c));
  const stageOptions = stages.map((s) => ({ label: s.is_terminal ? `${s.name} (terminal)` : s.name, value: s.id }));

  // --- deal value helpers ---
  const trackValue = !!(workflow && workflow.track_value);
  const fmtMoney = (n) => (n == null ? "" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
  const cardProb = (card) => {
    if (card.probability != null) return card.probability;
    const st = stages.find((s) => s.id === card.current_stage_id);
    if (!st) return 0;
    if (st.default_probability != null) return st.default_probability;
    if (st.is_terminal) return st.is_won ? 100 : 0;
    return 0;
  };
  const cardWeighted = (card) => (card.amount == null ? 0 : (card.amount * cardProb(card)) / 100);
  const sumBy = (arr, f) => arr.reduce((a, c) => a + (f(c) || 0), 0);
  const activeVisibleCards = cards.filter((c) => cardMatchesFilter(c) && c.status !== "completed");
  const openValue = sumBy(activeVisibleCards, (c) => c.amount || 0);
  const weightedValue = sumBy(activeVisibleCards, cardWeighted);

  // --- drag handlers ---
  const onDragStart = (e, cardId) => {
    e.dataTransfer.setData("cardId", cardId);
    e.dataTransfer.effectAllowed = "move";
    setDraggingCardId(cardId);
  };
  const onDragEnd = () => { setDraggingCardId(null); setDragOverStageId(null); };
  const onColumnDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStageId !== stageId) setDragOverStageId(stageId);
  };
  const onDrop = (e, stageId) => {
    e.preventDefault();
    setDragOverStageId(null);
    const cardId = e.dataTransfer.getData("cardId");
    setDraggingCardId(null);
    if (cardId) attemptMove(cardId, stageId);
  };

  const toolbar = (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "8px 8px", flexWrap: "wrap" }}>
      <div style={{ flexGrow: 1 }}>
        {workflow && (
          <Text type="secondary">
            Pipeline: <b>{workflow.pipeline_name || "—"}</b> · {workflow.card_count} card(s)
            {trackValue && (
              <> · Open <b>{fmtMoney(openValue)}</b> · Weighted <b>{fmtMoney(weightedValue)}</b></>
            )}
          </Text>
        )}
      </div>
      <Radio.Group type="button" value={viewMode} onChange={setViewMode}>
        <Radio value="board"><Tooltip content="Board"><IconApps /></Tooltip></Radio>
        <Radio value="list"><Tooltip content="List"><IconList /></Tooltip></Radio>
      </Radio.Group>
      <Checkbox checked={myCardsOnly} onChange={setMyCardsOnly}>My cards</Checkbox>
      <Checkbox checked={hideTerminal} onChange={setHideTerminal}>Hide terminal</Checkbox>
      <Button icon={<IconEdit />} onClick={openEdit}>Edit</Button>
      <Button type="primary" icon={<IconRefresh />} loading={syncing} onClick={syncNow}>Sync now</Button>
    </div>
  );

  const board = (
    <div style={{ display: "flex", gap: 12, overflowX: "auto", padding: "8px", alignItems: "flex-start" }}>
      {visibleStages.length === 0 && (
        <Text type="secondary">This pipeline has no stages. Add some in Settings → Pipelines.</Text>
      )}
      {visibleStages.map((stage) => {
        const stageCards = cardsForStage(stage.id);
        const isDropTarget = dragOverStageId === stage.id;
        return (
          <div
            key={stage.id}
            onDragOver={(e) => onColumnDragOver(e, stage.id)}
            onDrop={(e) => onDrop(e, stage.id)}
            style={{
              width: 260,
              flexShrink: 0,
              background: isDropTarget ? "var(--color-primary-light-1)" : "var(--color-fill-1)",
              outline: isDropTarget ? "2px dashed rgb(var(--arcoblue-4))" : "2px solid transparent",
              borderRadius: 8,
              padding: 8,
              minHeight: 120,
              transition: "background 0.12s, outline-color 0.12s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 6px 8px" }}>
              <Text style={{ fontWeight: 600 }}>
                {stage.name} {stage.is_terminal && <Tag size="small" color="gray" style={{ marginLeft: 4 }}>terminal</Tag>}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {trackValue && sumBy(stageCards, (c) => c.amount || 0) > 0 && (
                  <span style={{ marginRight: 6 }}>{fmtMoney(sumBy(stageCards, (c) => c.amount || 0))}</span>
                )}
                {stageCards.length}
              </Text>
            </div>

            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {stageCards.map((card) => {
                const dragging = draggingCardId === card.id;
                return (
                  <div
                    key={card.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, card.id)}
                    onDragEnd={onDragEnd}
                    onClick={() => openCard(card.id)}
                    style={{
                      background: "var(--color-bg-2)",
                      border: "1px solid var(--color-border-2)",
                      borderRadius: 6,
                      padding: "8px 10px",
                      cursor: "grab",
                      opacity: dragging ? 0.4 : 1,
                      boxShadow: dragging ? "0 6px 16px rgba(0,0,0,0.18)" : "none",
                      transition: "opacity 0.12s, box-shadow 0.12s",
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{card.contact_name || card.contact_email || "—"}</div>
                    {card.contact_name && (
                      <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>{card.contact_email}</div>
                    )}
                    <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {trackValue && card.amount != null && (
                        <Tag size="small" color="green">{fmtMoney(card.amount)}</Tag>
                      )}
                      {card.owner_user_id && (
                        <Tag size="small">{memberName(card.owner_user_id) || "owner"}</Tag>
                      )}
                    </div>
                  </div>
                );
              })}
              {stageCards.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--color-text-3)", padding: "4px 6px" }}>Drop cards here</div>
              )}
            </Space>
          </div>
        );
      })}
    </div>
  );

  const listData = cards.filter(cardMatchesFilter);
  const listColumns = [
    {
      title: "Contact",
      render: (_, card) => (
        <div>
          <div style={{ fontWeight: 500 }}>{card.contact_name || card.contact_email || "—"}</div>
          {card.contact_name && <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>{card.contact_email}</div>}
        </div>
      ),
    },
    {
      title: "Stage",
      width: 220,
      render: (_, card) => (
        // Stop propagation so using the dropdown doesn't also open the drawer.
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            size="small"
            style={{ width: 200 }}
            value={card.current_stage_id}
            onChange={(stageId) => attemptMove(card.id, stageId)}
            options={stageOptions}
          />
        </div>
      ),
    },
    { title: "Owner", width: 160, render: (_, card) => (card.owner_user_id ? memberName(card.owner_user_id) || "—" : <Text type="secondary">—</Text>) },
    ...(trackValue
      ? [{ title: "Amount", width: 120, render: (_, card) => (card.amount != null ? fmtMoney(card.amount) : <Text type="secondary">—</Text>) }]
      : []),
    { title: "Status", width: 120, render: (_, card) => <Tag size="small" color={card.status === "completed" ? "gray" : "green"}>{card.status}</Tag> },
  ];

  const list = (
    <div style={{ padding: "8px" }}>
      <Table
        size="small"
        rowKey="id"
        columns={listColumns}
        data={listData}
        pagination={false}
        noDataElement={<span style={{ color: "var(--color-text-3)" }}>No cards. Click “Sync now” to pull in the audience.</span>}
        onRow={(record) => ({ style: { cursor: "pointer" }, onClick: () => openCard(record.id) })}
      />
    </div>
  );

  return (
    <Content>
      <SimpleBreadcrumbs
        elements={[{ name: "Workflows", path: "/workflows" }, { name: (workflow && workflow.name) || "Workflow" }]}
      />

      {toolbar}
      {viewMode === "board" ? board : list}

      <CardDrawer
        visible={!!drawerCard}
        card={drawerCard}
        stages={stages}
        members={members}
        trackValue={trackValue}
        onClose={() => setDrawerCard(null)}
        onMove={(stageId) => attemptMove(drawerCard.id, stageId)}
        onAddNote={(note) => addNote(drawerCard.id, note)}
        onSetOwner={(userId) => setOwner(drawerCard.id, userId)}
        onSetValue={(amount, expectedClose, probability) => setValue(drawerCard.id, amount, expectedClose, probability)}
      />

      <Modal
        title="Edit workflow"
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        okText="Save"
        unmountOnExit
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Name</Text></div>
            <Input value={editName} onChange={setEditName} />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Assignees</Text></div>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Who works this board?"
              value={editAssignees}
              onChange={setEditAssignees}
              options={members.map((m) => ({ label: m.name || m.email, value: m.id }))}
            />
          </div>
          <Checkbox checked={editTrackValue} onChange={setEditTrackValue}>
            Track deal value (amounts &amp; forecast)
          </Checkbox>
          <Text type="secondary" style={{ fontSize: 12 }}>
            The pipeline and audience can't be changed after creation — create a new workflow for those.
          </Text>
        </Space>
      </Modal>

      <Modal
        title="Add a note for this move"
        visible={!!pendingMove}
        onCancel={() => setPendingMove(null)}
        onOk={confirmNoteMove}
        okText="Move"
        unmountOnExit
      >
        <Text type="secondary">This stage requires a note.</Text>
        <TextArea
          style={{ marginTop: 8 }}
          value={moveNote}
          onChange={setMoveNote}
          placeholder="Why is this card moving here?"
          autoSize={{ minRows: 2 }}
        />
      </Modal>
    </Content>
  );
}
